import React, { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Icon } from 'components';
import { Checkbox, Input } from 'components/form';
import MultiSelect from 'components/MultiSelect';
import { ButtonV3 } from 'components/ComponentV2';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useCommonList } from 'lib/contexts/common-list-context';
import { CompanyAttributes } from 'lib/models/company';
import { useSponsorCompanies } from 'lib/providers/SocietySponsorshipProvider';
import { OnboardSteps } from '../OnboardSteps/OnboardSteps.component';
import { RenderCompaniesDataCards } from './RenderCompaniesDataCards';

import styles from './ExploreCompanies.module.scss';

interface ExploreCompaniesProps {
  goBack: () => void;
  onMessageCompany: (company: CompanyAttributes) => void;
  onAddSponsor: (company: CompanyAttributes) => void;
  onInviteSponsor: (company: CompanyAttributes) => void;
  copyInvitationLink: () => void;
  onSelectPlan: () => void;
}

export const ExploreCompanies: React.FC<ExploreCompaniesProps> = (
  props: ExploreCompaniesProps
) => {
  const {
    goBack,
    onMessageCompany,
    copyInvitationLink,
    onAddSponsor,
    onInviteSponsor,
    onSelectPlan,
  } = props;
  const useBottomRef = useRef<HTMLDivElement | null>(null);
  const industryMapRef = useRef<{ [key: string]: string }>({});

  const [checkVerified, setCheckVerified] = useState<boolean>(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string[]>([]);
  const [selectedCompanySizes, setSelectedCompanySizes] = useState<string[]>(
    []
  );

  const {
    isCompaniesDataLoading,
    companiesData,
    searchText,
    fetchMoreData,
    setSearchText,
    setIndustryIds,
    setCompanySizes,
    setFilterByVerified,
  } = useSponsorCompanies();
  const { industryOptions = [], employeesCountRange: companySizeOptions = [] } =
    useCommonList();

  useVisibleOnIntersect({
    elRef: useBottomRef,
    threshold: 0,
    onIntersect: fetchMoreData,
    rootMargin: '250px',
  });

  useEffect(() => {
    if (industryOptions.length) {
      industryOptions.forEach((industry) => {
        industryMapRef.current[industry.label] = industry.id;
      });
    }
  }, [industryOptions]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const onSelectIndustry = (options: string[]) => {
    const ids = options.map((option) => industryMapRef.current[option]);
    setIndustryIds(ids);
    setSelectedIndustry(options);
  };

  const onSelectCompanySize = (options: string[]) => {
    setCompanySizes(options);
    setSelectedCompanySizes(options);
  };

  const toggleVerifiedCheck = () => {
    setCheckVerified(!checkVerified);
    setFilterByVerified(!checkVerified);
  };

  return (
    <div className={styles.ExploreCompanies}>
      <div className={styles.ExploreCompaniesHeader}>
        <div className={styles.breadcrumb} onClick={goBack}>
          <Icon size="large" iconName="chevron-left" />
          <p>Sponsorships</p>
        </div>
        <p className={styles.headerTitle}>Explore companies</p>
      </div>
      <div className={styles.ExploreCompaniesFilter}>
        <Input
          placeholder="Search"
          startIcon="icon_search"
          inputClassName={styles.searchBar}
          startIconClassName={styles.searchBarStartIcon}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <MultiSelect
          title="Industry"
          placeholder="Search industries"
          options={industryOptions}
          values={selectedIndustry}
          onChange={onSelectIndustry}
          iconClassName="icon_arrow-down"
          isSearchable
          triggerClassName={styles.filterButton}
        />
        <MultiSelect
          title="Company Size"
          options={companySizeOptions}
          values={selectedCompanySizes}
          onChange={onSelectCompanySize}
          iconClassName="icon_arrow-down"
          triggerClassName={styles.filterButton}
        />
        <div className={styles.verifiedFilter}>
          <p>Verified</p>
          <Checkbox
            size={20}
            className={cn(styles.verifiedFilterCheckbox, {
              [styles.uncheckedMode]: !checkVerified,
            })}
            checked={checkVerified}
            onChange={toggleVerifiedCheck}
          />
        </div>
      </div>
      <div className={styles.ExploreCompaniesList}>
        <RenderCompaniesDataCards
          onMessageCompany={onMessageCompany}
          onAddSponsor={onAddSponsor}
          onSelectPlan={onSelectPlan}
          onInviteSponsor={onInviteSponsor}
        />
      </div>
      {companiesData.length === 0 && searchText && !isCompaniesDataLoading && (
        <div className={styles.ExploreCompaniesEmpty}>
          <div>
            <p className={styles.emoji}>🙄</p>
            <p className={styles.emptyText}>
              {`We couldn't find "${searchText}". Use your invitation link to invite them to huzzle.`}
            </p>
            <div className={styles.copyLink}>
              <ButtonV3
                startIcon="icon_link"
                size="large"
                onClick={copyInvitationLink}
              >
                Copy invitation link
              </ButtonV3>
              <OnboardSteps />
            </div>
          </div>
        </div>
      )}
      <div ref={useBottomRef}></div>
    </div>
  );
};
